import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseDestroy } from 'nera-common/angular';
import { DOCUMENT } from '@angular/common';
import {
    Component,
    OnInit,
    Renderer2,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { FuseConfig, FuseConfigService } from '@fuse/services/config';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FusePlatformService } from '@fuse/services/platform';
import { FUSE_VERSION } from '@fuse/version';
import { combineLatest, filter, map } from 'rxjs';
import { AuthService } from 'app/core/services/auth.service';
import { EmptyLayoutComponent } from './layouts/empty/empty.component';
import { ClassyLayoutComponent } from './layouts/vertical/classy/classy.component';
import {
    applyScheme,
    applyTheme,
    deepestRoute,
    isLayout,
    resolveLayout,
    resolveScheme,
} from './layout.helpers';

@Component({
    selector: 'layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [EmptyLayoutComponent, ClassyLayoutComponent],
})
export class LayoutComponent extends BaseDestroy implements OnInit {
    private readonly _activatedRoute = inject(ActivatedRoute);
    private readonly _document = inject(DOCUMENT);
    private readonly _renderer2 = inject(Renderer2);
    private readonly _router = inject(Router);
    private readonly _fuseConfigService = inject(FuseConfigService);
    private readonly _fuseMediaWatcherService = inject(FuseMediaWatcherService);
    private readonly _fusePlatformService = inject(FusePlatformService);
    private readonly _authService = inject(AuthService);

    config: FuseConfig;
    layout: string;
    scheme: 'dark' | 'light';
    theme: string;

    ngOnInit(): void {
        // Restore user from token on refresh before layout resolves.
        this._authService.check().subscribe();

        combineLatest([
            this._fuseConfigService.config$,
            this._fuseMediaWatcherService.onMediaQueryChange$([
                '(prefers-color-scheme: dark)',
                '(prefers-color-scheme: light)',
            ]),
        ])
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                map(([config, mql]) => ({
                    scheme: resolveScheme(
                        config.scheme,
                        !!mql.breakpoints['(prefers-color-scheme: dark)']
                    ),
                    theme: config.theme,
                }))
            )
            .subscribe((options) => {
                this.scheme = options.scheme;
                this.theme = options.theme;
                applyScheme(this._document.body, this.scheme);
                applyTheme(this._document.body, this.theme);
            });

        this._fuseConfigService.config$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((config: FuseConfig) => {
                this.config = config;
                this._updateLayout();
            });

        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => this._updateLayout());

        // Re-evaluate public layout when login/logout happens on /public pages.
        this._authService.user$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (this._router.url.startsWith('/public')) {
                    this._updateLayout();
                }
            });

        this._renderer2.setAttribute(
            this._document.querySelector('[ng-version]'),
            'fuse-version',
            FUSE_VERSION
        );
        this._renderer2.addClass(
            this._document.body,
            this._fusePlatformService.osName
        );
    }

    private _updateLayout(): void {
        const route = deepestRoute(this._activatedRoute);
        const layoutFromQuery = route.snapshot.queryParamMap.get('layout');
        if (isLayout(layoutFromQuery) && this.config) {
            this.config.layout = layoutFromQuery;
        }

        this.layout = resolveLayout({
            configLayout: this.config.layout,
            route,
            url: this._router.url,
            user: this._authService.user,
        });
    }
}
